import type { Locality, PointOfInterest, TreeSummary } from '@api/types'
import { SpeciesSheet } from '@features/learn/SpeciesSheet'
import { useSpeciesList } from '@features/learn/useSpecies'
import { MagicStatusBadge } from '@features/magic/MagicStatusBadge'
import { ModerationActions } from '@features/magic/ModerationActions'
import type { MapMagicPlace } from '@features/magic/useMapMagicPlaces'
import { useMunicipalities } from '@features/places/usePlaces'
import { ReportForm } from '@features/report/ReportForm'
import { TreeSheet } from '@features/tree/TreeSheet'
import { useI18n } from '@i18n/I18nProvider'
import { directionsUrl } from '@lib/directions'
import { SheetHandle } from '@ui/BottomSheet'
import { Button, ButtonLink, buttonClasses } from '@ui/Button'
import { ConditionBadge } from '@ui/ConditionBadge'
import { Icon } from '@ui/Icon'
import { animationsOn, SHEET_EXIT_MS } from '@ui/motion'
import { Photo } from '@ui/Photo'
import { useCloseOnBack } from '@ui/useCloseOnBack'
import { useSheetMotion } from '@ui/useSheetMotion'
import { useEffect, useRef, useState } from 'react'
import type { MapSelection } from './MapView'

type Props = {
	selection: MapSelection | null
	trees: TreeSummary[]
	pois: PointOfInterest[]
	magicPlaces?: MapMagicPlace[]
	localities?: Locality[]
	onClose: () => void
}

// Un árbol tocado en el mapa: su especie y su estado, el aviso allí mismo (paso a paso), y la
// especie y la ficha completa del árbol en hojas encima, sin salir del mapa.
const TreeSheetContent = ({ tree }: { tree: TreeSummary }) => {
	const { t } = useI18n()
	const speciesList = useSpeciesList()
	const municipalities = useMunicipalities()
	const [speciesOpen, setSpeciesOpen] = useState(false)
	const [treeOpen, setTreeOpen] = useState(false)
	const species = speciesList.data?.items.find((s) => s.id === tree.speciesId)
	const regionId = municipalities.data?.items.find((m) => m.id === tree.municipalityId)?.regionId ?? null
	return (
		<>
			<div className="flex gap-3">
				{species?.photoUrl && (
					<Photo src={species.photoUrl} alt="" className="size-20 shrink-0 rounded-2xl object-cover" />
				)}
				<div className="min-w-0 pr-10">
					<p className="text-sm font-semibold text-earth-800">{t('tree.code', { code: tree.code })}</p>
					<h2 className="mb-2 text-2xl font-bold">
						{tree.speciesName ?? t('tree.code', { code: tree.code })}
					</h2>
					<ConditionBadge latest={tree.latestCondition} />
				</div>
			</div>
			<div className="mt-4">
				<ReportForm code={tree.code} channel="map" stepwise legend={t('tree.howIsIt')} />
			</div>
			<div className="mt-4 flex flex-col gap-2">
				<Button variant="secondary" fullWidth aria-haspopup="dialog" onClick={() => setSpeciesOpen(true)}>
					<Icon name="leaf" />
					{t('tree.aboutSpecies')}
				</Button>
				<Button variant="ghost" fullWidth aria-haspopup="dialog" onClick={() => setTreeOpen(true)}>
					<Icon name="tree" />
					{t('map.viewTree')}
				</Button>
			</div>
			{speciesOpen && (
				<SpeciesSheet id={tree.speciesId} regionId={regionId} onClose={() => setSpeciesOpen(false)} />
			)}
			{treeOpen && <TreeSheet code={tree.code} onClose={() => setTreeOpen(false)} />}
		</>
	)
}

// Una variante: qué es (un pueblo del Camino fuera del recorrido principal) y cómo llegar.
const VariantSheetContent = ({ variant }: { variant: Locality }) => {
	const { t } = useI18n()
	const directions = directionsUrl(variant.location)
	return (
		<>
			<div className="pr-10">
				<p className="text-sm font-semibold text-earth-800">{t('map.variant')}</p>
				<h2 className="text-xl font-bold break-words">{variant.name}</h2>
				<p className="mt-1 text-earth-800">{t('map.variantText')}</p>
			</div>
			{directions && (
				<div className="mt-4">
					<a
						href={directions}
						target="_blank"
						rel="noopener noreferrer"
						className={buttonClasses('secondary', true)}
					>
						<Icon name="route" />
						{t('places.directions')}
						<Icon name="external" className="size-5" />
					</a>
				</div>
			)}
		</>
	)
}

const EXIT_STYLE = { transform: 'translateY(calc(100% + 1rem))', transition: `transform ${SHEET_EXIT_MS}ms ease-in` }

// Hoja inferior con lo tocado en el mapa: un árbol (con su aviso y su especie), un lugar de
// interés con acceso a su ficha, un lugar mágico, con "Cómo llegar" y, si hay sesión de
// gestor, sus acciones, o una variante. Sube desde abajo y se cierra con su botón,
// arrastrándola hacia abajo por el asa o con el "atrás" del móvil.
export const SelectionSheet = ({ selection, trees, pois, magicPlaces = [], localities = [], onClose }: Props) => {
	const { t } = useI18n()
	// Si la selección se quita desde fuera (p. ej. tocando el mapa), la tarjeta sigue con lo último
	// que mostraba mientras baja, y después se va. Si la cierra ella misma, ya bajó: se va en el acto.
	const [lastShown, setLastShown] = useState(selection)
	if (selection && selection !== lastShown) setLastShown(selection)
	const leaving = !selection && lastShown !== null
	const closedHere = useRef(false)
	useEffect(() => {
		if (!leaving) return
		if (closedHere.current || !animationsOn()) {
			closedHere.current = false
			setLastShown(null)
			return
		}
		const timer = setTimeout(() => setLastShown(null), SHEET_EXIT_MS)
		return () => clearTimeout(timer)
	}, [leaving])
	const shown = selection ?? lastShown
	const tree = shown?.kind === 'tree' ? trees.find((x) => x.code === shown.code) : undefined
	const poi = shown?.kind === 'poi' ? pois.find((x) => x.id === shown.id) : undefined
	const magic = shown?.kind === 'magic' ? magicPlaces.find((x) => x.id === shown.id) : undefined
	const variant = shown?.kind === 'variant' ? localities.find((x) => x.id === shown.id && x.variant) : undefined
	const { requestClose, style, dragHandlers } = useSheetMotion(() => {
		closedHere.current = true
		onClose()
	})
	// El "atrás" del móvil cierra la hoja en vez de salir del mapa (mientras baja, ya no está abierta).
	useCloseOnBack(!leaving && Boolean(tree || poi || magic || variant), requestClose)
	if (!tree && !poi && !magic && !variant) return null
	const magicDirections = magic ? directionsUrl(magic.location) : null

	return (
		<section
			aria-live="polite"
			data-sheet=""
			data-leaving={leaving || undefined}
			style={leaving ? EXIT_STYLE : style}
			className={`${leaving ? 'pointer-events-none ' : ''}absolute inset-x-2 bottom-2 z-10 mx-auto max-h-[calc(100%-1rem)] max-w-xl animate-sheet-in overflow-y-auto overscroll-contain rounded-3xl bg-white px-4 pb-4 shadow-lg motion-reduce:animate-none`}
		>
			{/* Asa: arrastrarla hacia abajo cierra la tarjeta (sin desplazar la página). */}
			<div {...dragHandlers} data-testid="sheet-grip" className="-mx-4 touch-none select-none pb-1">
				<SheetHandle />
			</div>
			<button
				type="button"
				onClick={requestClose}
				aria-label={t('map.close')}
				className="absolute top-2 right-2 flex size-12 items-center justify-center rounded-full text-earth-800"
			>
				<Icon name="close" />
			</button>
			{/* `key`: al tocar otro árbol, el aviso empieza de cero. */}
			{tree && <TreeSheetContent key={tree.code} tree={tree} />}
			{variant && <VariantSheetContent variant={variant} />}
			{poi && (
				<>
					<div className="flex gap-3">
						{poi.photoUrls[0] && (
							<Photo
								src={poi.photoUrls[0]}
								alt=""
								className="size-24 shrink-0 rounded-2xl object-cover"
							/>
						)}
						<div className="pr-10">
							<p className="text-sm font-semibold text-earth-800">{t(`poi.${poi.category}`)}</p>
							<h2 className="text-xl font-bold">{poi.name}</h2>
							<p className="mt-1 line-clamp-3 text-earth-800">{poi.description}</p>
						</div>
					</div>
					<div className="mt-4">
						<ButtonLink href={`/lugares/${encodeURIComponent(poi.id)}`} fullWidth>
							<Icon name="pin" />
							{t('map.viewPlace')}
						</ButtonLink>
					</div>
				</>
			)}
			{magic && (
				<>
					<div className="flex gap-3">
						<Photo src={magic.photoUrl} alt="" className="size-24 shrink-0 rounded-2xl object-cover" />
						<div className="min-w-0 pr-10">
							<p className="text-sm font-semibold text-earth-800">{t('poi.magic_place')}</p>
							<h2 className="text-xl font-bold break-words">{magic.name}</h2>
							{magic.proposal && (
								<div className="mt-1 flex flex-col items-start gap-1">
									<MagicStatusBadge status={magic.status} />
									<p className="text-sm break-all text-earth-800">
										{magic.proposal.anonymousId
											? t('magic.anonymous', { id: magic.proposal.anonymousId })
											: t('magic.noAnonymous')}
									</p>
								</div>
							)}
						</div>
					</div>
					<div className="mt-4 flex flex-col gap-3">
						{magicDirections && (
							<a
								href={magicDirections}
								target="_blank"
								rel="noopener noreferrer"
								className={buttonClasses('secondary', true)}
							>
								<Icon name="route" />
								{t('places.directions')}
								<Icon name="external" className="size-5" />
							</a>
						)}
						{magic.proposal && (
							<ModerationActions id={magic.id} status={magic.status} onDeleted={onClose} />
						)}
					</div>
				</>
			)}
		</section>
	)
}
